import { Button } from '@/components/form/Button';
import { ButtonVariant } from '@/components/form/buttonHelper';
import { Loading } from '@/components/loading/Loading';
import { Modal } from '@/components/modal/Modal';
import { ModalProps, useModal } from '@/components/modal/useModal';
import { Card } from '@/components/sections/Card';
import SiPlus from '@/icons/SiPlus';
import { User } from '@/services/api/commonTypes';
import { useDeletePartnerUser, usePartnerUsers } from '@/services/api/hooks/partners';

import { FunctionComponent, Suspense } from 'react';
import InviteUsersToMspModal from './InviteUsersToMspModal';

const ManageMspUsersModal: FunctionComponent<ModalProps<{ mspId: string }>> = props => {
  const DisableMsp = () => {
    const mspId = props.props?.mspId;
    if (!mspId) throw new Error('Missing partner id');

    const [users] = usePartnerUsers(mspId);

    const deleteUser = useDeletePartnerUser();

    const onDelete = async (id: string) => {
      await deleteUser.mutateAsync({ partnerId: mspId, id });
    };

    const errorObj = deleteUser.error as Error | undefined;
    const error: string = errorObj ? errorObj.message : '';

    const onMspInvite = () => inviteMspModalProps.open({ mspId });

    const inviteMspModalProps = useModal<{ mspId: string }>();

    return (
      <>
        <InviteUsersToMspModal {...inviteMspModalProps} />
        <div className="mt-5 space-y-4">
          <Button onClick={onMspInvite} className="ml-auto">
            <SiPlus className="text-xl" />
          </Button>

          {users.items.map((user: User) => (
            <div className="flex justify-between space-x-3 items-center" key={user.id}>
              <div>
                <p key={user.id} className="font-medium">
                  {user.givenName} {user.surname}
                </p>

                <p>{user.email}</p>
              </div>

              {error && <p className="text-red-500">{error}</p>}

              <Button
                className="h-fit"
                onClick={async () => {
                  await onDelete(user.id);
                }}
                loading={deleteUser.isLoading}
                variant={ButtonVariant.Destructive}
              >
                Delete
              </Button>
            </div>
          ))}
        </div>
      </>
    );
  };

  return (
    <Modal className="md:max-w-xl" {...props}>
      <Card className="w-full p-8">
        <Modal.Controls {...props}>
          <h1 className="text-2xl font-semibold">Manage users</h1>
        </Modal.Controls>

        <Suspense fallback={<Loading />}>
          <DisableMsp />
        </Suspense>
      </Card>
    </Modal>
  );
};

export default ManageMspUsersModal;
